import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  getCoreDomain,
  returnLinkForDownload,
  customHeaders,
  customHeadersLD,
  authPatch,
} from '@probatix/common/services/services.helpers'
import {
  basePathPatientEvents,
  partial,
  testAppointmentsEndpoint,
  timeFilter,
} from '@probatix/common/services/RTKServices/patientEvents/index.helpers'
import { DocumentPayload, PatientEvent, Tests } from '@probatix/common/services/RTKServices/patientEvents/index.model'
import { CORE_DOMAIN } from '@probatix/common/services/services.constants'

import LocalStorage from '@probatix/common/utils/localStorage'

// Define a service using a base URL and expected endpoints
export const patientEventsApi = createApi({
  baseQuery: fetchBaseQuery({ baseUrl: getCoreDomain() }),
  endpoints: (builder) => ({
    deletePatientEvent: builder.mutation<PatientEvent, { uuid: string }>({
      query: ({ uuid }) => ({
        headers: customHeaders(),
        method: 'DELETE',
        url: `${basePathPatientEvents}/${uuid}`,
      }),
    }),
    getAppointments: builder.query<any, any>({
      query: ({ getTotalItem, page }) => ({
        headers: customHeadersLD(),
        method: 'GET',
        url: `${testAppointmentsEndpoint}${page}${getTotalItem ? partial : ''}${timeFilter}`,
      }),
    }),
    getDocPatientEvents: builder.query<DocumentPayload, any>({
      query: ({ type, uuid }) => ({
        headers: customHeaders(),
        method: 'GET',
        url: `${basePathPatientEvents}/${uuid}/documents/${type}`,
      }),
    }),
    getResultSingle: builder.query<any, any>({
      query: ({ uuid }) => ({
        headers: customHeaders(),
        method: 'GET',
        url: `${basePathPatientEvents}/${uuid}`,
      }),
      transformResponse: (responseData: Tests) => {
        if (responseData?.testCenter?.baseUrl) {
          LocalStorage.set(CORE_DOMAIN, returnLinkForDownload(responseData.testCenter.baseUrl))
        }

        return responseData
      },
    }),
    getStartedPatientEvents: builder.query<any, any>({
      query: ({ page }) => ({
        headers: customHeadersLD(),
        method: 'GET',
        url: `${basePathPatientEvents}?orderBy[appointmentTime]=desc&status[]=started&page=${page}`,
      }),
    }),
    patchPatientEvent: builder.mutation<any, any>({
      query: ({ appointmentTime, uuid }) => ({
        body: JSON.stringify({
          appointmentTime: appointmentTime,
        }),
        headers: {
          ...authPatch(),
          ...{
            'Content-Type': 'application/merge-patch+json',
          },
        },
        method: 'PATCH',
        url: `${basePathPatientEvents}/${uuid}`,
      }),
    }),
  }),
  reducerPath: 'patientEventsApi',
})

export const useGetPatientEventSingle = patientEventsApi.endpoints.getResultSingle.useLazyQuery
export const useGetDocPatientEvents = patientEventsApi.endpoints.getDocPatientEvents.useLazyQuery
export const useGetStartedPatientEvents = patientEventsApi.endpoints.getStartedPatientEvents.useLazyQuery
export const usePatchPatientEvents = patientEventsApi.endpoints.patchPatientEvent.useMutation
export const useDeletePatientEvent = patientEventsApi.endpoints.deletePatientEvent.useMutation
